import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '../../layout/Card.jsx';
import { Icon } from '../../layout/Icon.jsx';
import { api, apiError } from '../../shared/api.js';
import { useSocketEvent } from '../../shared/socket.js';
import { toast } from '../../shared/useToast.js';
import { printReceipt } from '../../shared/printReceipt.js';
import { roundUpToStep } from '../../shared/pricingClient.js';
import { useRoundUpTo } from '../../shared/useSettings.js';
import { LookupBar } from './components/LookupBar.jsx';
import { ChildSummary } from './components/ChildSummary.jsx';
import { GroupMembersList } from './components/GroupMembersList.jsx';
import { BillBreakdown } from './components/BillBreakdown.jsx';
import { PaymentBox } from './components/PaymentBox.jsx';
import { OverrideBox } from './components/OverrideBox.jsx';
import { SiblingDiscountToggle } from './components/SiblingDiscountToggle.jsx';
import { SettleConfirm } from './components/SettleConfirm.jsx';
import { ReceiptPreviewModal } from './components/ReceiptPreviewModal.jsx';
import { useCheckout } from './hooks/useCheckout.js';
import { useSettleGroup, printCheckoutReceipt } from './hooks/useSettle.js';

export default function CheckoutScreen({ sessionId: routeSessionId = null }) {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const sessionId = useCheckout((s) => s.sessionId);
  const setSessionId = useCheckout((s) => s.setSessionId);
  const clearSession = useCheckout((s) => s.clearSession);
  const selectedIds = useCheckout((s) => s.selectedIds);
  const setSelectedIds = useCheckout((s) => s.setSelectedIds);
  const toggleSelected = useCheckout((s) => s.toggleSelected);
  const payment = useCheckout((s) => s.payment);
  const override = useCheckout((s) => s.override);
  const setPayment = useCheckout((s) => s.setPayment);
  const toggleOverride = useCheckout((s) => s.toggleOverride);
  const resetOverride = useCheckout((s) => s.resetOverride);
  const skipSibling = useCheckout((s) => s.skipSibling);
  const setSkipSibling = useCheckout((s) => s.setSkipSibling);
  const receiptOpen = useCheckout((s) => s.receiptOpen);
  const receiptData = useCheckout((s) => s.receiptData);
  const openReceipt = useCheckout((s) => s.openReceipt);
  const closeReceipt = useCheckout((s) => s.closeReceipt);

  const roundStep = useRoundUpTo();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [focusedId, setFocusedId] = useState(null);
  // أول طفل باقٍ على الكارت بعد التسوية — نعود إليه بدل شاشة البحث.
  const [remainingSessionId, setRemainingSessionId] = useState(null);

  // sync URL <-> store
  useEffect(() => {
    if (routeSessionId && routeSessionId !== sessionId) {
      setSessionId(routeSessionId);
    }
    if (!routeSessionId && sessionId) {
      clearSession();
    }
  }, [routeSessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // مجموعة الكارت: كل الأطفال النشطين + مستحقات كل واحد، محسوبة في الخادم.
  const groupQuery = useQuery({
    queryKey: ['checkout', 'group', sessionId, skipSibling],
    enabled: !!sessionId,
    queryFn: async () => {
      const { data } = await api.get(`/pos/suspended/group/by-session/${sessionId}`, {
        params: skipSibling ? { skipSiblingDiscount: 1 } : undefined
      });
      return data?.data || data;
    },
    refetchInterval: 60_000,
    staleTime: 5_000
  });

  const group = groupQuery.data;
  const members = useMemo(
    () => (Array.isArray(group?.members) ? group.members : []),
    [group]
  );

  // عند تحميل مجموعة جديدة: نختار الطفل المطلوب فقط، والتركيز عليه.
  useEffect(() => {
    if (!members.length) return;
    const ids = members.map((m) => m.session._id);
    const stillValid = selectedIds.filter((id) => ids.includes(id));
    if (!stillValid.length) {
      setSelectedIds(ids.includes(sessionId) ? [sessionId] : [ids[0]]);
    } else if (stillValid.length !== selectedIds.length) {
      setSelectedIds(stillValid);
    }
    if (!focusedId || !ids.includes(focusedId)) {
      setFocusedId(ids.includes(sessionId) ? sessionId : ids[0]);
    }
  }, [members]); // eslint-disable-line react-hooks/exhaustive-deps

  // Realtime invalidation
  useSocketEvent('child:active', 'invoice_added', (payload) => {
    if (!sessionId) return;
    const sid = payload?.sessionId || payload?.session?._id;
    const known = members.some((m) => m.session._id === sid);
    if (sid && sid !== sessionId && !known) return;
    qc.invalidateQueries({ queryKey: ['checkout', 'group', sessionId] });
    toast.info('تمت إضافة فاتورة جديدة');
  }, [sessionId, members]);

  useSocketEvent('child:active', 'settled', (payload) => {
    if (!sessionId) return;
    const sid = payload?.sessionId || payload?.session?._id;
    const known = members.some((m) => m.session._id === sid);
    if (sid && sid !== sessionId && !known) return;
    qc.invalidateQueries({ queryKey: ['checkout', 'group', sessionId] });
  }, [sessionId, members]);

  const focused = useMemo(
    () => members.find((m) => m.session._id === focusedId) || members[0] || null,
    [members, focusedId]
  );

  const selected = useMemo(
    () => members.filter((m) => selectedIds.includes(m.session._id)),
    [members, selectedIds]
  );

  // الإجماليات تُجمَع من أرقام الخادم مباشرة، فيرى الكاشير نفس ما سيُحصَّل.
  const totals = useMemo(() => {
    const sum = (fn) => selected.reduce((s, m) => s + Number(fn(m.totals) || 0), 0);

    const playDue = sum((t) => t.playDue);
    const playPaid = sum((t) => t.playPaid);
    const companionsDue = sum((t) => t.companionsDue);
    const companionsPaid = sum((t) => t.companionsPaid);
    const cafeteria = sum((t) => t.cafeteria);
    const toys = sum((t) => t.toys);
    const otherDue = sum((t) => t.otherDue);
    const rawDue = sum((t) => t.due);

    // أساس الخصم = رسوم اللعب والدخول المستحقة فقط.
    // فواتير نقطة البيع (الكافتيريا/البارستا والألعاب) لا يُطبَّق عليها الخصم.
    const discountMax = sum((t) => t.discountableDue);
    const discount = override.active && Number(override.amount) > 0
      ? Math.max(0, Math.min(Math.round(Number(override.amount)), discountMax))
      : 0;

    const paid = playPaid + companionsPaid;
    const grandRaw = Math.max(
      0,
      playDue + playPaid + companionsDue + companionsPaid + cafeteria + toys + otherDue - discount
    );
    // تقريب المستحق للأعلى بنفس خطوة الخادم، فيرى الكاشير الرقم الذي سيُحصَّل.
    const dueRaw = Math.max(0, rawDue - discount);
    const due = roundUpToStep(dueRaw, roundStep);
    const rounding = Math.max(0, due - dueRaw);
    const grand = grandRaw + rounding;

    return {
      play: playDue,
      playPaid,
      companionsDue,
      companionsPaid,
      companionsCount: sum((t) => t.companionsCount),
      cafeteria,
      toys,
      otherDue,
      posDue: cafeteria + toys + otherDue,
      discount,
      discountMax,
      rounding,
      grand,
      paid,
      due,
      membersCount: selected.length,
      totalMembers: members.length,
      minutes: Number(focused?.totals?.minutes || 0)
    };
  }, [selected, members.length, override, focused, roundStep]);

  const cardBalance = Number(group?.card?.balance || 0);
  const hasCard = !!group?.cardUid;

  // Settle
  const settle = useSettleGroup({
    onSuccess: (data) => {
      const change = Math.max(0, Number(payment.paidAmount || 0) - Number(totals.due || 0));
      const stillInside = members.find((m) => !selectedIds.includes(m.session._id));
      setRemainingSessionId(stillInside ? stillInside.session._id : null);
      openReceipt({
        session: focused?.session,
        sessions: selected.map((m) => m.session),
        child: focused?.child,
        guardian: group?.guardian,
        payment: { ...payment },
        totals: { ...totals, paidBefore: totals.paid, change },
        result: data
      });
      const left = Number(data?.remainingActive || 0);
      toast.success(
        left > 0
          ? `تمت التسوية • ما زال ${left} طفل داخل القاعة على نفس الكارت`
          : 'تمت التسوية • يمكنك طباعة الإيصال'
      );
    }
  });

  function buildSettleBody() {
    const isCredit = payment.method === 'credit';
    const paidNow = isCredit
      ? 0
      : Math.round(Math.min(Number(payment.paidAmount || 0), Number(totals.due) || 0));
    const breakdown = {
      play: totals.play,
      cafeteria: totals.cafeteria,
      toys: totals.toys
    };
    if (skipSibling) {
      breakdown.skipSiblingDiscount = true;
    }
    if (totals.discount > 0) {
      // نرسل القيمة بعد التقييد بأساس الخصم، لا ما كتبه المستخدم.
      breakdown.discount = totals.discount;
      breakdown.discountReason = override.reason || '';
    }
    // نقص المبلغ: نرسل قرار الكاشير (إسقاط/دين) فيُغلق الخادم الجلسة بدل الرفض.
    const short = !isCredit && paidNow < Number(totals.due || 0) ? payment.shortfall : null;
    if (short === 'waive' && !breakdown.discountReason) {
      breakdown.discountReason = override.reason || '';
    }
    return {
      sessionIds: selectedIds,
      paymentMethod: payment.method,
      paidAmount: paidNow,
      cashboxId: payment.method === 'cash' ? payment.cashboxId : undefined,
      shortfall: short || undefined,
      breakdown
    };
  }

  function handleSettleClick() {
    if (!selectedIds.length) {
      toast.error('اختر طفلاً واحداً على الأقل');
      return;
    }
    if (payment.method === 'cash' && !payment.cashboxId) {
      toast.error('اختر القاصة أولاً');
      return;
    }
    if (totals.discount > 0 && (override.reason || '').trim().length < 3) {
      toast.error('اكتب سبب الخصم');
      return;
    }
    const isCredit = payment.method === 'credit';
    const paidNow = Number(payment.paidAmount || 0);
    if (!isCredit && paidNow < Number(totals.due || 0)) {
      if (!payment.shortfall) {
        toast.error('المبلغ أقل من المستحق — اختر إسقاط الفرق أو تسجيله ديناً');
        return;
      }
      if (payment.shortfall === 'waive' && (override.reason || '').trim().length < 3) {
        toast.error('اكتب سبب إسقاط الفرق');
        return;
      }
    }
    setConfirmOpen(true);
  }

  async function handleConfirm() {
    try {
      await settle.mutateAsync(buildSettleBody());
      setConfirmOpen(false);
    } catch {
      // error toast handled by useApiMutation
    }
  }

  async function handlePrint() {
    // الإيصال الحراري لكل طفل على حدة — المعاينة تغطي المجموعة كلها، فلو
    // طبعنا الطفل المركَّز عليه فقط لخرج إيصال أنقص مما وافق عليه الكاشير.
    const settledSessions = Array.isArray(receiptData?.sessions) && receiptData.sessions.length
      ? receiptData.sessions.map((s) => s._id)
      : [focused?.session?._id || sessionId];
    const ids = settledSessions.filter(Boolean);
    if (!ids.length) return;

    setPrinting(true);
    let printedCount = 0;
    try {
      for (const id of ids) {
        // eslint-disable-next-line no-await-in-loop
        const data = await printCheckoutReceipt(id).catch(() => null);
        if (data?.printed) printedCount += 1;
      }
    } finally {
      setPrinting(false);
    }

    if (printedCount === ids.length) {
      toast.success(ids.length > 1 ? `تم إرسال ${ids.length} إيصالات للطابعة` : 'تم إرسال الإيصال للطابعة');
    } else {
      // فشل الطابعة الحرارية غير قاتل — نطبع المعاينة عبر المتصفح
      printReceipt();
    }
  }

  function handleCloseReceipt() {
    const remaining = Number(receiptData?.result?.remainingActive || 0);
    const nextId = remainingSessionId;
    closeReceipt();
    resetOverride();
    setPayment({ paidAmount: 0, method: 'cash', shortfall: null });

    // كارت مشترك ما زال عليه أطفال: نبقى على نفس المجموعة بدل العودة للبحث،
    // حتى يستطيع الكاشير إخراج الطفل التالي مباشرةً.
    if (remaining > 0 && nextId) {
      setSessionId(nextId);
      setFocusedId(nextId);
      navigate(`/checkout/${nextId}`, { replace: true });
      qc.invalidateQueries({ queryKey: ['checkout', 'group', nextId] });
      return;
    }

    clearSession();
    navigate('/checkout', { replace: true });
  }

  function handlePick(id) {
    if (!id) return;
    setPayment({ paidAmount: 0, shortfall: null });
    resetOverride();
    setSkipSibling(false);
    setSessionId(id);
    setFocusedId(id);
    navigate(`/checkout/${id}`, { replace: true });
  }

  const loading = groupQuery.isLoading;
  const error = groupQuery.error;

  return (
    <div className="space-y-3.5">
      <LookupBar onPick={handlePick} />

      {!sessionId ? (
        <Card>
          <div className="text-center py-12 text-ink-3 flex flex-col items-center gap-2">
            <Icon name="checkout" size={48} color="#D6CCB3" />
            <div className="font-bold text-ink-2">امسح كارت الطفل أو ابحث بالاسم لبدء التسوية</div>
            <div className="text-[12px]">سيتم عرض كل الأطفال على الكارت مع رسوم اللعب والفواتير المعلّقة</div>
          </div>
        </Card>
      ) : loading ? (
        <Card>
          <div className="text-center py-10 text-ink-3">جاري تحميل بيانات الجلسة...</div>
        </Card>
      ) : error || !focused ? (
        <Card>
          <div className="text-center py-8">
            <div className="text-red font-bold mb-2">تعذّر تحميل الجلسة</div>
            <div className="text-[12px] text-ink-3 mb-3">
              {error ? apiError(error) : 'الجلسة غير موجودة أو منتهية'}
            </div>
            <button
              type="button"
              className="btn"
              onClick={() => { clearSession(); navigate('/checkout', { replace: true }); }}
            >
              العودة للبحث
            </button>
          </div>
        </Card>
      ) : (
        <div
          className="grid gap-3.5"
          style={{ gridTemplateColumns: 'minmax(300px, 1fr) minmax(360px, 1.4fr) minmax(300px, 1fr)' }}
        >
          <div className="space-y-3">
            <GroupMembersList
              group={group}
              selectedIds={selectedIds}
              focusedId={focusedId}
              onToggle={toggleSelected}
              onFocus={setFocusedId}
              onSelectAll={() => setSelectedIds(members.map((m) => m.session._id))}
              onClearAll={() => setSelectedIds([])}
            />
            <ChildSummary session={focused.session} />
            <SiblingDiscountToggle session={focused.session} />
            <OverrideBox totals={totals} />
          </div>
          <div>
            <BillBreakdown
              members={selected}
              focused={focused}
              totals={totals}
            />
          </div>
          <div className="self-start sticky" style={{ top: 12 }}>
            <PaymentBox
              totals={totals}
              cardBalance={cardBalance}
              hasCard={hasCard}
              onSettle={handleSettleClick}
              onToggleOverride={() => toggleOverride()}
              settleLoading={settle.isPending}
            />
          </div>
        </div>
      )}

      <SettleConfirm
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
        totals={totals}
        payment={payment}
        override={override}
        loading={settle.isPending}
      />

      <ReceiptPreviewModal
        open={receiptOpen}
        onClose={handleCloseReceipt}
        onPrint={handlePrint}
        data={receiptData}
        printing={printing}
      />
    </div>
  );
}
